import { Heading, Text, VStack } from '@chakra-ui/react';
import type { HeadingProps, ResponsiveValue, TextProps } from '@chakra-ui/react';
import type { ReactNode } from 'react';

/** About V2 sections share the Thmanyah type ramp (see {@link HeroSection}). */
const FONT_FAMILY = "'Thmanyah Sans', 'Noto Sans Arabic', sans-serif";

export type SectionHeaderAlign = 'start' | 'center';

export interface SectionHeaderProps {
  /** Small accent label above the title (e.g. قصتنا، فيش، من فعالياتنا). */
  eyebrow: ReactNode;
  /** Bold section title. */
  title: ReactNode;
  /** Optional supporting line under the title. */
  subtitle?: ReactNode;
  /** Horizontal alignment of the block. Defaults to 'start'. */
  align?: SectionHeaderAlign;
  /** Optional id for the title heading, e.g. to wire up `aria-labelledby`. */
  titleId?: string;
  /** Optional max width for the title, to match the design's title box. */
  titleMaxW?: ResponsiveValue<string>;
  /** Per-section typography overrides for the title heading. */
  titleProps?: HeadingProps;
  /** Per-section typography overrides for the eyebrow label. */
  eyebrowProps?: TextProps;
}

/**
 * Shared section header (TZ-379): a small coral eyebrow, a bold title, and an
 * optional subtitle. Used across most About V2 sections (Story, partners, etc.).
 * RTL, with centered and start-aligned variants via `align`.
 */
export const SectionHeader = ({
  eyebrow,
  title,
  subtitle,
  align = 'start',
  titleId,
  titleMaxW,
  titleProps,
  eyebrowProps,
}: SectionHeaderProps) => {
  const isCentered = align === 'center';
  return (
    <VStack
      dir='rtl'
      w='100%'
      align={isCentered ? 'center' : 'flex-start'}
      textAlign={isCentered ? 'center' : 'start'}
      spacing={{ base: '8px', md: '12px' }}
    >
      <Text
        as='p'
        m={0}
        color='#F2563C' // accent-coral (About V2 eyebrow)
        fontFamily={FONT_FAMILY}
        fontWeight='400'
        fontSize='18px'
        lineHeight='160%'
        letterSpacing='0'
        {...eyebrowProps}
      >
        {eyebrow}
      </Text>

      <Heading
        as='h2'
        id={titleId}
        m={0}
        maxW={titleMaxW}
        color='#1B1917' // text-primary
        fontFamily={FONT_FAMILY}
        fontWeight='400'
        fontSize={{ base: '30px', md: '48px' }}
        lineHeight='119%'
        letterSpacing='0'
        {...titleProps}
      >
        {title}
      </Heading>

      {subtitle != null && (
        <Text
          as='p'
          m={0}
          color='dark.D500'
          fontFamily={FONT_FAMILY}
          fontWeight='400'
          fontSize={{ base: '15px', md: '18px' }}
          lineHeight='160%'
        >
          {subtitle}
        </Text>
      )}
    </VStack>
  );
};
